/**
 * The category tag on a vendor row.
 *
 * Shared rather than duplicated because the two vendor lists on the compose
 * screen — the recipient field's dropdown and the rolodex grid behind it — are
 * meant to read as two views of one list, and this pill plus the row's name and
 * address is the whole of that vocabulary. It was identical in both files by
 * coincidence of two people typing the same five classes, which is not a thing
 * that survives the next edit to either one.
 *
 * Whether to show it at all stays with the caller: each list hides the pill
 * while it is confined to a single category, and each knows a different way of
 * being so confined.
 */
export default function VendorCategoryPill({ category }: { category: string | null }) {
  // Callers hold a `primaryCategory` that is frequently null, and every one of
  // them would otherwise write the same guard.
  if (!category) return null;

  return (
    // Outlined rather than filled, because the row's hover or highlight state
    // already owns a surface — and sage rather than muted grey for the same
    // reason. At 11px the threshold is 4.5:1, and the two callers put this text
    // on four different backgrounds: the dimmed primary holds 7.68:1 at rest in
    // both lists, 6.07:1 on the grid's hover surface and 5.90:1 on the
    // dropdown's highlight, so it clears the bar everywhere with room to spare.
    // Muted grey would read 3.59:1 and 3.50:1 on those same two active
    // surfaces, which is under the bar on the states a row spends its
    // attention in.
    <span className="label-caps shrink-0 rounded-full border border-[var(--color-outline-variant)] px-2 py-0.5 text-[0.6875rem] text-[var(--color-primary-dim)]">
      {category}
    </span>
  );
}
